import { useMemo, useRef, useState } from 'react';
import { executeFetchAsync } from '../Util.js';
import { config } from '../Const.js';

const AI_SUGGESTION_COUNT = 10;

export default function useQuizAiSuggestions({
    getSubjectForField,
    onApplySuggestionToField,
    onApplyPrefetchedSuggestionsToField
}) {
    const [aiRunning, setAiRunning] = useState(false);

    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const [aiLoading, setAiLoading] = useState({});
    const [aiResults, setAiResults] = useState([]);
    const [aiFieldName, setAiFieldName] = useState(null);
    const [aiSelectedOption, setAiSelectedOption] = useState("");
    const [aiError, setAiError] = useState("");
    const [aiCache, setAiCache] = useState({});

    // Ref mirrors aiSelectedOption so applyAiSuggestion always reads the
    // current value regardless of whether React has flushed the state batch.
    const aiSelectedOptionRef = useRef("");

    // Use this everywhere instead of calling setAiSelectedOption directly,
    // so the ref and state are never out of step.
    const setAiSelectedOptionSynced = (val) => {
        aiSelectedOptionRef.current = val;
        setAiSelectedOption(val);
    };

    const safeTrim = (value) => (typeof value === "string" ? value.trim() : "");
    const valueOrEmpty = (value) => value ?? '';

    const resetAiState = () => {
        setAiDialogOpen(false);
        setAiLoading({});
        setAiResults([]);
        setAiFieldName(null);
        setAiSelectedOptionSynced("");
        setAiError("");
        setAiCache({});
    };

    const normalizeScore01 = (val) => {
        if (val == null) return null;
        const num = Number(val);
        if (Number.isNaN(num)) return null;
        if (num > 1) return Math.max(0, Math.min(1, num / 100));
        return Math.max(0, Math.min(1, num));
    };

    const extractCorrectnessFromRaw = (raw) => {
        if (!raw) return { correctnessScore: null, correctnessResponse: null };

        const possibleScoreKeys = ['correctness_score', 'correctnessScore'];
        const possibleRespKeys = ['correctness_response', 'correctnessResponse'];

        let score = null;
        let resp = null;

        for (const k of possibleScoreKeys) {
            if (raw[k] != null) {
                score = normalizeScore01(raw[k]);
                break;
            }
        }

        for (const k of possibleRespKeys) {
            if (raw[k] != null) {
                resp = String(raw[k]);
                break;
            }
        }

        return { correctnessScore: score, correctnessResponse: resp };
    };

    const getAICacheKey = (field) => {
        if (field === 'dyk1' || field === 'dyk2') return 'dyk';
        if (field === 'qa1' || field === 'qa2') return 'qa';
        return field;
    };

    const aiEndpointForField = (field) => {
        if (field === 'quizName') return "/api/v1/admin/AI/generate-quiz-title";
        if (field === 'seoTitle') return "/api/v1/admin/AI/generate-seo-slug";
        if (field === 'description') return "/api/v1/admin/AI/generate-meta-description";
        if (field === 'dyk1' || field === 'dyk2' || field === 'dyk') return "/api/v1/admin/AI/generate-dyk";
        if (field === 'qa1' || field === 'qa2' || field === 'qa') return "/api/v1/admin/AI/generate-qa";
        return null;
    };

    const parseAiResult = (field, result) => {
        if (!result) return [];

        if (field === 'dyk1' || field === 'dyk2' || field === 'dyk') {
            let parsed = null;

            if (typeof result === 'string') {
                try {
                    parsed = JSON.parse(result);
                } catch {
                    parsed = null;
                }
            } else if (typeof result === 'object') {
                parsed = result;
            }

            const factArray = parsed && Array.isArray(parsed.original_facts)
                ? parsed.original_facts
                : parsed && Array.isArray(parsed.filtered_facts)
                    ? parsed.filtered_facts
                    : null;

            const rawScores = parsed && Array.isArray(parsed.scores) ? parsed.scores : [];

            if (factArray && factArray.length > 0) {
                return factArray
                    .map((fact, i) => {
                        const text = String(fact ?? "").trim();
                        const raw = rawScores[i];
                        let score = null;
                        let explanation = null;

                        if (raw != null) {
                            if (raw.score01 != null && !Number.isNaN(Number(raw.score01))) {
                                score = Number(raw.score01);
                            }
                            if (raw.explanation != null) {
                                explanation = String(raw.explanation);
                            }
                        }

                        return { text, score, explanation };
                    })
                    .filter(it => it.text);
            }

            return [];
        }

        if (field === 'qa1' || field === 'qa2' || field === 'qa') {
            let parsed = null;

            if (typeof result === 'string') {
                try {
                    parsed = JSON.parse(result);
                } catch {
                    parsed = null;
                }
            } else if (typeof result === 'object') {
                parsed = result;
            }

            const qaArray = parsed && Array.isArray(parsed.original_qas)
                ? parsed.original_qas
                : parsed && Array.isArray(parsed.filtered_qas)
                    ? parsed.filtered_qas
                    : parsed && Array.isArray(parsed.qas)
                        ? parsed.qas
                        : Array.isArray(parsed)
                            ? parsed
                            : null;

            if (qaArray && qaArray.length > 0) {
                return qaArray
                    .map((qItem) => {
                        if (!qItem || typeof qItem !== 'object') return null;

                        const extracted = extractCorrectnessFromRaw(qItem);
                        const questionText = (qItem.question ?? qItem.q ?? qItem.prompt ?? "").toString().trim();
                        const answerText = (qItem.answer ?? qItem.a ?? qItem.response ?? "").toString().trim();
                        const explanation = (qItem.explanation ?? qItem.note ?? qItem.context ?? "").toString().trim();

                        const combined = (questionText || answerText)
                            ? `Q: ${questionText}\nA: ${answerText}${explanation ? `\n\n${explanation}` : ''}`
                            : JSON.stringify(qItem);

                        return {
                            text: combined,
                            score: extracted.correctnessScore,
                            explanation,
                            correctnessScore: extracted.correctnessScore,
                            correctnessResponse: extracted.correctnessResponse
                        };
                    })
                    .filter(Boolean)
                    .filter(it => it.text);
            }

            return [];
        }

        if (typeof result === 'string') {
            return result
                .split('|')
                .map(p => ({ text: p.trim(), score: null }))
                .filter(it => it.text);
        }

        if (Array.isArray(result)) {
            return result
                .map(el => {
                    if (typeof el === 'string') return { text: el.trim(), score: null };
                    if (el && typeof el === 'object') {
                        const text = el.text || el.title || JSON.stringify(el);
                        const score = typeof el.score === 'number' ? el.score : null;
                        const explanation = el.explanation ?? null;
                        return { text: String(text).trim(), score, explanation };
                    }
                    return { text: String(el).trim(), score: null };
                })
                .filter(it => it.text);
        }

        try {
            const s = JSON.stringify(result);
            return s
                .split('|')
                .map(p => ({ text: p.trim(), score: null }))
                .filter(it => it.text);
        } catch {
            return [];
        }
    };

    const setAiLoadingFlag = (cacheKey, isLoading) => {
        setAiLoading(prev => ({ ...(prev || {}), [cacheKey]: isLoading }));
    };

    const createAIFormData = (subjectText, count = AI_SUGGESTION_COUNT) => {
        const fd = new FormData();
        fd.append("subject", subjectText);
        fd.append("count", count);
        return fd;
    };

    const fetchAiSuggestions = async ({
        field,
        subjectText,
        forceRefresh = false,
        openDialog = false,
        count = AI_SUGGESTION_COUNT
    }) => {
        const endpoint = aiEndpointForField(field);
        const cacheKey = getAICacheKey(field);

        if (!endpoint) {
            if (openDialog) {
                setAiFieldName(field);
                setAiError("No AI endpoint configured for this field.");
                setAiResults([]);
                setAiSelectedOptionSynced("");
                setAiDialogOpen(true);
            }
            return [];
        }

        const safeSubject = safeTrim(subjectText);
        if (!safeSubject) {
            if (openDialog) {
                setAiFieldName(field);
                setAiError("Please enter a subject first.");
                setAiResults([]);
                setAiSelectedOptionSynced("");
                setAiDialogOpen(true);
            }
            return [];
        }

        const cached = aiCache?.[cacheKey];
        if (!forceRefresh && cached && cached.length > 0) {
            if (openDialog) {
                setAiFieldName(field);
                setAiResults(cached);
                setAiSelectedOptionSynced(cached[0]?.text || "");
                setAiError("");
                setAiDialogOpen(true);
            }
            return cached;
        }

        setAiError("");
        if (openDialog) {
            setAiFieldName(field);
            setAiResults([]);
            setAiSelectedOptionSynced("");
            setAiDialogOpen(true);
        }

        setAiLoadingFlag(cacheKey, true);

        try {
            const result = await executeFetchAsync(
                config.SERVER_URL + endpoint,
                "post",
                null,
                createAIFormData(safeSubject, count),
                null
            );

            const parsed = parseAiResult(field, result);

            setAiCache(prev => ({ ...(prev || {}), [cacheKey]: parsed }));

            if (openDialog) {
                setAiResults(parsed);
                setAiSelectedOptionSynced(parsed[0]?.text || "");
                if (!parsed.length) {
                    setAiError("No results from AI.");
                }
            }

            return parsed;
        } catch (err) {
            console.warn(`AI request failed for ${field}`, err);

            if (openDialog) {
                setAiResults([]);
                setAiSelectedOptionSynced("");
                setAiError("AI request failed.");
            }

            return [];
        } finally {
            setAiLoadingFlag(cacheKey, false);
        }
    };

    const applyAiSuggestion = (field, value) => {
        if (!field) return;
        // Read from the ref when no explicit value is passed — avoids stale
        // closure issues where React hasn't flushed the aiSelectedOption state
        // update yet (most visible on seoTitle's fast-parsing pipe-split path).
        const resolvedValue = value !== undefined ? value : aiSelectedOptionRef.current;
        onApplySuggestionToField(field, valueOrEmpty(resolvedValue));
        closeAiDialog();
    };

    const prefetchAllAiSuggestions = async ({
        titleSubject,
        descriptionSubject,
        slugSubject,
        dykSubject,
        qaSubject,
        forceRefresh = true,
        applyDefaults = true
    }) => {
        const tasks = [
            ['quizName', titleSubject],
            ['description', descriptionSubject],
            ['seoTitle', slugSubject],
            ['dyk', dykSubject],
            ['qa', qaSubject]
        ];

        setAiRunning(true);

        const suggestionMap = {};

        try {
            await Promise.allSettled(
                tasks.map(async ([field, subjectText]) => {
                    const suggestions = await fetchAiSuggestions({
                        field,
                        subjectText,
                        forceRefresh,
                        openDialog: false,
                        count: AI_SUGGESTION_COUNT
                    });

                    suggestionMap[field] = suggestions;

                    if (applyDefaults) {
                        onApplyPrefetchedSuggestionsToField(field, suggestions);
                    }

                    return suggestions;
                })
            );

            return suggestionMap;
        } finally {
            setAiRunning(false);
        }
    };

    const openAiDialogForField = async (field) => {
        const subjectText = safeTrim(getSubjectForField(field));

        await fetchAiSuggestions({
            field,
            subjectText,
            forceRefresh: false,
            openDialog: true,
            count: AI_SUGGESTION_COUNT
        });
    };

    const refreshAiDialogForField = async () => {
        if (!aiFieldName) return;

        const subjectText = safeTrim(getSubjectForField(aiFieldName));

        await fetchAiSuggestions({
            field: aiFieldName,
            subjectText,
            forceRefresh: true,
            openDialog: true,
            count: AI_SUGGESTION_COUNT
        });
    };

    const closeAiDialog = () => {
        setAiDialogOpen(false);
        setAiResults([]);
        setAiFieldName(null);
        setAiSelectedOptionSynced("");
        setAiError("");
    };

    const dialogCacheKey = useMemo(
        () => (aiFieldName ? getAICacheKey(aiFieldName) : null),
        [aiFieldName]
    );

    const loadedFromCache = Boolean(dialogCacheKey && aiCache?.[dialogCacheKey]?.length);

    const formatScorePct = (val01) => {
        if (val01 == null) return "—";
        return `${Math.round(val01 * 100)}%`;
    };

    return {
        aiRunning,
        aiDialogOpen,
        aiLoading,
        aiResults,
        aiFieldName,
        aiSelectedOption,
        aiError,
        aiCache,
        dialogCacheKey,
        loadedFromCache,
        resetAiState,
        prefetchAllAiSuggestions,
        openAiDialogForField,
        refreshAiDialogForField,
        closeAiDialog,
        applyAiSuggestion,
        // Expose the synced setter so the dialog's onChange also keeps the ref current
        setAiSelectedOption: setAiSelectedOptionSynced,
        formatScorePct
    };
}